/**
 * Iter273.2 — BetaBanner
 *
 * Beta-tester reassurance strip rendered above pricing + checkout
 * surfaces. Tells testers the plans they're seeing are "live" only
 * in the sense that the marketing copy is finalized — but no card
 * is charged automatically during the beta, and a confirmation
 * step is required before anything bills.
 *
 * Single source of truth so we can flip the BETA flag globally
 * when we exit beta and remove all banners with one constant.
 *
 * Drop in at the top of any page where testers might worry about
 * "looking too live":
 *
 *     import BetaBanner from "../components/BetaBanner";
 *     ...
 *     <BetaBanner />
 *
 * Use `variant="compact"` for inline placement (e.g. inside an
 * existing card) and `variant="full"` for top-of-page strips.
 */
import React from "react";
import { Sparkles } from "lucide-react";

// Flip to `false` (and remove the imports) when QorTrace exits beta.
export const IN_BETA = true;

export default function BetaBanner({ variant = "full", message }) {
  if (!IN_BETA) return null;

  const text = message
    || "QorTrace is in private beta. Pricing pages are live for review, but no order will charge a card without a separate confirmation. Explore freely — break things, then ‘Report issue’ in the admin footer.";

  if (variant === "compact") {
    return (
      <div
        data-testid="beta-banner-compact"
        role="note"
        style={{
          display: "inline-flex", alignItems: "center", gap: 6,
          padding: "4px 10px", borderRadius: 999,
          background: "rgba(255, 214, 10, 0.10)",
          border: "1px solid rgba(255, 214, 10, 0.30)",
          color: "#FFD60A",
          fontFamily: "ui-monospace,Menlo,monospace",
          fontSize: 10, letterSpacing: "0.16em",
          textTransform: "uppercase",
        }}
      >
        <Sparkles size={10} aria-hidden="true" />
        Beta · No auto-charge
      </div>
    );
  }

  return (
    <div
      data-testid="beta-banner"
      role="note"
      style={{
        background: "linear-gradient(90deg, rgba(255, 214, 10, 0.08) 0%, rgba(255, 214, 10, 0.03) 100%)",
        borderBottom: "1px solid rgba(255, 214, 10, 0.20)",
        color: "var(--qt-text-1, #E5EDF7)",
        padding: "10px 24px",
        fontSize: 13, lineHeight: 1.5,
        display: "flex", alignItems: "center",
        gap: 12, justifyContent: "center",
      }}
    >
      <span style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        padding: "3px 10px", borderRadius: 999,
        background: "rgba(255, 214, 10, 0.16)",
        color: "#FFD60A",
        fontFamily: "ui-monospace,Menlo,monospace",
        fontSize: 10, letterSpacing: "0.16em",
        textTransform: "uppercase", flexShrink: 0,
      }}>
        <Sparkles size={10} aria-hidden="true" />
        Beta
      </span>
      <span style={{ color: "var(--qt-text-2, #A5B5CE)" }}>
        {text}
      </span>
    </div>
  );
}
